const DEFAULT_IMS_CLIENT_ID1 = '11f12c992f8d1723a1ba4126c2944235';
const DEFAULT_IMS_CLIENT_ID = 'availability-tracker';
const DEFAULT_IMS_SCOPE = 'AdobeID,openid';
const DEFAULT_IMS_LOCALE = 'en_US';
const IMS_SCRIPT_ID = 'adobe-ims-script';
const DEFAULT_IMS_ENVIRONMENT = 'prod';
const DEFAULT_IMS_DEBUG = false;

const IMS_SCRIPT_URLS = {
  prod: 'https://auth.services.adobe.com/imslib/imslib.min.js',
  stg1: 'https://auth-stg1.services.adobe.com/imslib/imslib.min.js'
} as const;

let imsLoadPromise: Promise<AdobeIMSClient> | null = null;
let cachedProfile: AdobeIMSProfile | null = null;

function parseEmailAllowList(value?: string): Set<string> {
  return new Set(
    (value || '')
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  return new Error('Adobe IMS authentication failed.');
}

function getConfiguredEnvironment(): 'prod' | 'stg1' {
  return DEFAULT_IMS_ENVIRONMENT === 'prod' ? 'prod' : 'stg1';
}

function getScriptUrl(): string {
  return (
    IMS_SCRIPT_URLS[getConfiguredEnvironment()]
  );
}

function isDebugEnabled(): boolean {
  if (DEFAULT_IMS_DEBUG != null) {
    return DEFAULT_IMS_DEBUG === 'true';
  }

  return import.meta.env.DEV;
}

function getTokenFromIms(): string | null {
  try {
    return window.adobeIMS?.getAccessToken?.() || null;
  } catch (error) {
    console.error('Failed to read IMS access token.', error);
    return null;
  }
}

async function getProfileFromIms(): Promise<AdobeIMSProfile | null> {
  const profileGetter = window.adobeIMS?.getProfile;

  if (!profileGetter) {
    return cachedProfile;
  }

  try {
    const result = profileGetter();

    if (result && typeof (result as PromiseLike<AdobeIMSProfile>).then === 'function') {
      const profile = await result;
      cachedProfile = profile;
      return profile;
    }

    cachedProfile = result || null;
    return cachedProfile;
  } catch (error) {
    console.error('Failed to read IMS profile.', error);
    return cachedProfile;
  }
}

function pickFirstString(
  source: Record<string, unknown> | null | undefined,
  keys: string[]
): string | null {
  if (!source) {
    return null;
  }

  for (const key of keys) {
    const value = source[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function buildSession(profile: AdobeIMSProfile | null, accessToken: string | null): AdobeImsSession {
  const adminEmails = [];
  const editorEmails = [];
  const email =
    pickFirstString(profile, ['email'])?.toLowerCase() ||
    pickFirstString(profile, ['userId'])?.toLowerCase() ||
    null;
  const displayName =
    pickFirstString(profile, ['displayName', 'name', 'givenName']) ||
    email ||
    'Adobe user';
  const isSignedIn = Boolean(accessToken);
  const hasAllowLists = adminEmails.size > 0 || editorEmails.size > 0;

  let accessLevel: AdobeImsAccessLevel = 'anonymous';

  if (isSignedIn) {
    if (email && adminEmails.has(email)) {
      accessLevel = 'admin';
    } else if (email && (editorEmails.has(email) || adminEmails.has(email))) {
      accessLevel = 'editor';
    } else {
      accessLevel = hasAllowLists ? 'editor' : 'editor';
    }
  }

  return {
    accessLevel,
    accessToken,
    canManageData: accessLevel === 'admin' || accessLevel === 'editor',
    isSignedIn,
    user: isSignedIn ?
    {
      displayName,
      email,
      rawProfile: profile
    } :
    null
  };
}

function configureAdobeId() {
  window.adobeid = {
    client_id: DEFAULT_IMS_CLIENT_ID,
    scope: DEFAULT_IMS_SCOPE,
    locale: DEFAULT_IMS_LOCALE,
    environment: getConfiguredEnvironment(),
    useLocalStorage: false,
    autoValidateToken: true,
    debug: isDebugEnabled(),
    api_parameters: {
      authorize: {
        response_type: 'token id_token',
        redirect_uri: window.location.href
      }
    },
    onReady(appState: unknown) {
      console.info('IMS ready', appState);
      void syncAdobeImsSession();
    },
    onProfile(profile: AdobeIMSProfile) {
      cachedProfile = profile;
      console.info('IMS profile', profile);
      window.myAppAuth?.onLogin(buildSession(profile, getTokenFromIms()));
    },
    onError(error: unknown) {
      const normalizedError = normalizeError(error);
      console.error('IMS error', normalizedError);
      window.myAppAuth?.onAuthError?.(normalizedError);
    }
  };
}

function loadImsScript(scriptUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      IMS_SCRIPT_ID
    ) as HTMLScriptElement | null;

    const handleLoad = () => resolve();
    const handleError = () => reject(new Error('Unable to load the Adobe IMS library.'));

    if (existingScript) {
      if (window.adobeIMS) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = IMS_SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });
}

export async function ensureAdobeIms(): Promise<AdobeIMSClient> {
  if (window.adobeIMS) {
    return window.adobeIMS;
  }

  if (!imsLoadPromise) {
    configureAdobeId();
    imsLoadPromise = loadImsScript(getScriptUrl()).then(() => {
      if (!window.adobeIMS) {
        throw new Error('Adobe IMS loaded without exposing the global client.');
      }

      return window.adobeIMS;
    });
  }

  return imsLoadPromise;
}

export async function syncAdobeImsSession(): Promise<AdobeImsSession> {
  await ensureAdobeIms();
  const [profile, accessToken] = await Promise.all([
    getProfileFromIms(),
    Promise.resolve(getTokenFromIms())
  ]);
  const session = buildSession(profile, accessToken);

  if (session.isSignedIn) {
    window.myAppAuth?.onLogin(session);
  } else {
    window.myAppAuth?.onLogout?.();
  }

  return session;
}

export function getAdobeAccessToken(): string | null {
  return getTokenFromIms();
}

export async function signInWithAdobe(): Promise<void> {
  const ims = await ensureAdobeIms();
  ims.signIn();
}

export async function signOutFromAdobe(): Promise<void> {
  const ims = await ensureAdobeIms();
  cachedProfile = null;
  ims.signOut();
  window.myAppAuth?.onLogout?.();
}
